'use client';

import { useState } from 'react';

import { useSearchParams } from 'next/navigation';

import clsx from 'clsx';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Search24Thick } from '@/modules/common/components/icons';
import {
  MobileSearchOverlay,
  SearchDropdownPanel,
  SearchInputField,
  SearchProvider,
  useDesktopDropdownOpenedTracking,
  useSearch,
} from '@/modules/common/components/search';
import { searchBarOpenedDataLayerEvent } from '@/modules/common/services/data-layer-events/search-bar-opened';

type SERPSearchBarSimplifiedProps = {
  className?: string;
  testIdPrefix?: string;
};

/**
 * SERP search bar. Wraps a `SearchProvider` and renders:
 *  - Desktop: inline combobox (hidden below md)
 *  - Mobile: fake trigger button + full-screen overlay (hidden at md+)
 *
 * Both are SSR-rendered; visibility is toggled via Tailwind classes.
 */
export function SERPSearchBarSimplified({
  className,
  testIdPrefix = 'serp',
}: SERPSearchBarSimplifiedProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const searchParams = useSearchParams();
  const hasExistingQuery = !!searchParams?.get('query');

  return (
    <SearchProvider
      where={DataLayerWhere.Search}
      syncWithUrl
      preserveSearchParams
      searchParams={searchParams}
      onAfterNavigate={() => setIsMobileOpen(false)}
    >
      <SERPSearchBarContent
        className={className}
        isMobileOpen={isMobileOpen}
        onMobileOpenChange={setIsMobileOpen}
        hasExistingQuery={hasExistingQuery}
        testIdPrefix={testIdPrefix}
      />
    </SearchProvider>
  );
}

function SERPSearchBarContent({
  className,
  isMobileOpen,
  onMobileOpenChange,
  hasExistingQuery,
  testIdPrefix,
}: {
  className?: string;
  isMobileOpen: boolean;
  onMobileOpenChange: (v: boolean) => void;
  hasExistingQuery: boolean;
  testIdPrefix: string;
}) {
  const search = useSearch();
  const showDesktopDropdown = search.isOpen && search.hasPanelContent;

  useDesktopDropdownOpenedTracking({ where: 'serp', hasExistingQuery });

  const handleMobileOpenChange = (open: boolean) => {
    onMobileOpenChange(open);
    if (!open) {
      search.setIsOpen(false);
    }
  };

  const handleMobileTriggerClick = () => {
    searchBarOpenedDataLayerEvent({
      where: 'serp',
      device: 'mobile',
      recent_searches_count: search.recentSearches.length,
      has_existing_query: hasExistingQuery,
    });
    handleMobileOpenChange(true);
  };

  return (
    <div className={clsx('w-full', className)} data-testid={`${testIdPrefix}-search-bar`}>
      <div ref={search.containerRef} className="relative hidden w-full md:block">
        <SearchInputField
          showClear
          placeholder="Search Samples..."
          ariaControls="search-suggestions-desktop"
          testId={`${testIdPrefix}-search-input`}
        />
        {showDesktopDropdown && (
          <SearchDropdownPanel
            id="search-suggestions-desktop"
            className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg"
          />
        )}
      </div>

      <button
        type="button"
        onClick={handleMobileTriggerClick}
        className="relative flex w-full items-center rounded-lg border border-gray-200 bg-white py-2.5 pl-12 pr-4 text-left text-base md:hidden"
        aria-label="Open search"
        data-testid={`${testIdPrefix}-search-bar-mobile-trigger`}
      >
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
          <Search24Thick />
        </span>
        <span className={clsx('truncate', search.value ? 'text-foreground' : 'text-gray-400')}>
          {search.value || 'What can we help you find?'}
        </span>
      </button>

      <MobileSearchOverlay
        open={isMobileOpen}
        onOpenChange={handleMobileOpenChange}
        inputTestId={`${testIdPrefix}-search-overlay-input`}
      />
    </div>
  );
}
